<template>
  <cms-collection as-list :class="{ 'b-b': bottomBorder }" :content="mappedContent" />
</template>

<script lang="ts" setup>
import type { Target } from '#types/target'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{ content: any }>()

const { $feature, $gtm } = useNuxtApp()
const { activeButtonMapping, activeClassOverride } = useAppConfig().components.cms.sharedButton
const { bottomBorder, highlightButton } = content

const route = useRoute()

const section = inject(SectionContextKey, null)

const addTextLinkStyles = (targets, content) => targets.map((target) => ({ ...target, ...getTextLinkStyles(content) }))

const selectActiveButton = (targets: Target[]) => {
  if (!highlightButton) return targets

  const sortedTargets = targets.toSorted((a, b) => (b.url?.length || 0) - (a.url?.length || 0))

  for (const target of sortedTargets) {
    if (!target.url || !target.style || !target.url.startsWith('/')) continue

    const [path, query] = target.url.split('?')
    const type = target.type

    if (type === 'CMExternalChannel') {
      if (getUrlId(route.path, 'Category') !== target.categoryId) continue
    }
    else if (type === 'CMExternalProduct') {
      if (getUrlId(route.path, 'Product') !== target.productId) continue
    }
    else if (route.path !== path) {
      continue
    }

    if (!matchQuery(query, route.query)) continue

    adjustActiveButtonStyle(target, activeButtonMapping, activeClassOverride)

    break
  }

  return targets
}

const getSlideMaxWidthForBreakpoint = (br: string) => {
  if (content.breakpoints[br].type === 'carousel')
    return br === 'sm' ? '19.25rem' : '26.5rem'

  return null
}

const handleButtonClick = ({ productId, text }: Target) => {
  if (productId) $gtm.push('pdpPage.onSelectStyle', `${productId} - ${text}`)
}

const mappedContent = ref()

const remapData = () => {
  mappedContent.value = {
    ...content,
    breakpoints: Object.assign({}, ...Object.keys(content.breakpoints).map((br) => ({
      [br]: {
        ...content.breakpoints[br],
        slidePadding: content.breakpoints[br].slidePadding ?? 'small',
        showArrows: br !== 'sm',
        slideMaxWidth: getSlideMaxWidthForBreakpoint(br)
      }
    }))),
    cardsAlignment: $feature.cmsCtaCollectionEqualSize && 'full',
    items: selectActiveButton(addTextLinkStyles(content.targets || [], content)).map((target) => ({
      id: target.id,
      type: 'VfCanvasCTAButton',
      ...(section?.name === 'below-sizes' && content.variant === 'flex' && { onItemClick: handleButtonClick }),
      target: {
        ...target,
        size: {
          sm: {
            size: target.size?.sm?.size ?? content.ctaSize ?? 'sm',
            fullWidth: true
          },
          md: {
            size: target.size?.md?.size ?? content.ctaSize ?? 'md'
          },
          lg: {
            size: target.size?.lg?.size ?? content.ctaSize ?? 'md'
          }
        }
      }
    }))
  }
}

remapData()

highlightButton && watch(() => route.fullPath, remapData)
</script>
